/**
 * ADLSectionForms
 * CCH Care — Phase 3 → Phase 5d
 *
 * Named exports: RepositioningForm, FluidIntakeForm,
 * BowelUrinationForm, SkinChecksForm, SafetyChecksForm
 *
 * Phase 5d changes:
 * - All relevant forms accept isNA, naReason, attestation,
 *   onAttest, onRetractAttestation props
 * - ADLSectionNA component wired into each supported section
 * - N/A sections render locked with N/A banner, no form content
 * - BowelUrinationForm accepts split attestation props per sub-section
 *
 * N/A wiring completion (2026-06-08):
 * - FluidIntakeForm, BowelUrinationForm (per sub-section), and
 *   SafetyChecksForm now consume the care-plan isNA flag, matching the
 *   Repositioning/SkinChecks pattern. Previously these received `disabled`
 *   (which folds isSectionNA) but no `isNA`, so their card header fell
 *   through to "In Progress" while the progress bar showed N/A.
 *
 * Attested-section Amend gate (2026-05-19):
 * - Each ADLSectionCard now receives attested={!!attestation}. When a
 *   section was signed via the "Nothing to Report" attestation it has
 *   no charted rows, so ADLSectionCard hides the Amend button (the
 *   amend modal would otherwise open to "No entries found"). Correcting
 *   a wrong attestation is the retract flow in ADLSectionNA, not amend.
 *   Bowel and Urination pass their own per-sub-section attestation.
 *
 * Autofill + invalid-autosave hardening (2026-06-24):
 * - autoComplete="off" on every free-text input/textarea in this file.
 *   Browser/password-manager autofill was dropping the logged-in email
 *   into bare <input type="text"> notes fields (no name/autocomplete),
 *   which flipped a section dirty and triggered an autosave. On urination
 *   that produced a 400 ("output_type is required") because the only
 *   populated field was notes. Killing autofill at the field level stops
 *   the spurious dirty state — and stops stray PII landing in a clinical
 *   notes column (relevant for facility 2 / DPA-2020 real-resident data).
 * - The urination useADLForm now passes validate: (d) => !!d.output_type.
 *   The backend requires output_type unconditionally (sections.php
 *   adl_save_urination), so without it the POST can only 400. The
 *   predicate makes save() a no-op until output_type is set: nothing is
 *   sent, the form stays dirty, and the next field change re-arms autosave
 *   once the required field is present. Pairs with the retry-suspension
 *   guard added to useADLForm (2026-06-24).
 */

import { useState, useCallback, useEffect } from 'react';
import { COLORS, POSITIONS, FLUID_TYPES, FLUID_PRESETS, BOWEL_SIZES, URINE_TYPES, SAFETY_CATEGORIES } from '../../config/constants';
import { saveRepositioning, saveFluidIntake, saveBowel, saveUrination, saveSkinCheck, saveSafetyChecks } from '../../api/adl';
import useADLForm from '../../hooks/useADLForm';
import ADLSectionCard from '../../components/ui/ADLSectionCard';
import ADLAmendmentBanner from '../../components/ui/ADLAmendmentBanner';
import ADLSectionNA from '../../components/ui/ADLSectionNA';

import { formatTime } from '../../utils/formatters';
// ── Shared styles ────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 6,
  border: `1px solid ${COLORS.border}`, fontSize: 13,
  color: COLORS.text, boxSizing: 'border-box',
  background: COLORS.surface, fontFamily: 'inherit',
};

const cancelBtnStyle = {
  flex: 1, padding: '9px 0', borderRadius: 8,
  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
  fontSize: 13, fontWeight: 600, color: COLORS.textMuted, cursor: 'pointer',
};

function saveBtnStyle(isDisabled) {
  return {
    flex: 2, padding: '9px 0', borderRadius: 8, border: 'none',
    background: isDisabled ? COLORS.border : COLORS.primary,
    fontSize: 13, fontWeight: 700,
    color: isDisabled ? COLORS.textMuted : '#fff',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
  };
}

function editBtnStyle() {
  return {
    padding: '3px 8px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
    background: COLORS.surface, fontSize: 11, color: COLORS.textMuted,
    cursor: 'pointer', lineHeight: 1.4,
  };
}

// ── Shared micro-components ──────────────────────────────────

function ToggleChip({ label, active, onToggle, color }) {
  return (
    <button onClick={onToggle} style={{
      padding: '5px 12px', borderRadius: 20, fontSize: 12,
      fontWeight: 600, cursor: 'pointer',
      background: active ? color + '20' : COLORS.surface,
      color: active ? color : COLORS.textMuted,
      border: `1px solid ${active ? color : COLORS.border}`,
    }}>
      {active ? '✓ ' : ''}{label}
    </button>
  );
}

function ChipRow({ label, options, value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>{label}</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map(opt => (
          <button key={opt.key} onClick={() => onChange(value === opt.key ? null : opt.key)}
            style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
              background: value === opt.key ? COLORS.primary : COLORS.surface,
              color: value === opt.key ? '#fff' : COLORS.textMuted,
              border: `1px solid ${value === opt.key ? COLORS.primary : COLORS.border}`,
            }}>{opt.label}</button>
        ))}
      </div>
    </div>
  );
}

const ASSIST_OPTIONS  = ['independent', '1_person', '2_person', 'mechanical_lift', 'refused'];
const ASSIST_LABELS   = {
  independent: 'Independent', '1_person': '1 Person',
  '2_person': '2 Person', mechanical_lift: 'Mech. Lift', refused: 'Refused',
};
const POSITION_LABELS = {
  left: 'Left Side', right: 'Right Side', back: 'Supine',
  elevated: 'Elevated HOB', chair: 'Chair/OOB',
};

const EMPTY_REPO_FORM  = { position: null, assist_level: null, was_refused: false, skin_intact: true, notes: '' };
const EMPTY_FLUID_FORM = { fluid_type: null, amount_ml: '', method: 'oral', refused: false, notes: '' };
const EMPTY_SKIN_FORM  = { result: 'pass', body_location: '', description: '', wound_care_performed: false, wound_care_notes: '', skin_cream_applied: false, nurse_notified: false };

const CONSISTENCY_OPTIONS = ['Normal', 'Hard', 'Soft', 'Loose', 'Liquid'];
const BM_COLOR_OPTIONS    = ['Brown', 'Black', 'Red', 'Clay', 'Yellow', 'Green'];
const URINE_COLORS        = ['Clear', 'Yellow', 'Dark Yellow', 'Amber', 'Orange', 'Red', 'Cloudy'];


// ============================================================
// RepositioningForm
// Phase 5d: supports isNA flag + nothing-to-report attestation
// ============================================================

export function RepositioningForm({
  adlRecordId, initialData = [], sectionStatus, amendments,
  onSign, disabled, onDataSaved,
  // Phase 5d props
  isNA = false, naReason = '',
  attestation = null, onAttest, onRetractAttestation,
}) {
  const [entries, setEntries]       = useState(initialData);
  const [adding, setAdding]         = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState(EMPTY_REPO_FORM);

  function setField(k, v) { setForm(p => ({ ...p, [k]: v })); }

  function startAdd() {
    setEditTarget(null);
    setForm(EMPTY_REPO_FORM);
    setAdding(true);
  }

  function startEdit(entry) {
    setEditTarget(entry);
    setForm({
      position:     entry.position    ?? null,
      assist_level: entry.assist_level ?? null,
      was_refused:  !!entry.was_refused,
      skin_intact:  entry.skin_intact !== false && entry.skin_intact !== 0,
      notes:        entry.notes ?? '',
    });
    setAdding(true);
  }

  function cancelForm() {
    setAdding(false);
    setEditTarget(null);
    setForm(EMPTY_REPO_FORM);
  }

  async function handleSave() {
    if (!form.position && !form.was_refused) return;
    try {
      setSaving(true);
      const payload = {
        ...form,
        scheduled_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
      };
      if (editTarget) payload.adl_repo_id = editTarget.adl_repo_id;

      const data = await saveRepositioning(adlRecordId, payload);
      if (data.success) {
        if (editTarget) {
          setEntries(prev => prev.map(e =>
            e.adl_repo_id === editTarget.adl_repo_id
              ? { ...e, ...form, performed_at: new Date().toISOString() }
              : e
          ));
        } else {
          setEntries(prev => [...prev, { adl_repo_id: data.adl_repo_id, ...form, performed_at: new Date().toISOString() }]);
        }
        cancelForm();
        onDataSaved?.();
      }
    } catch (err) {
      console.error('Repositioning save failed:', err.message);
    } finally {
      setSaving(false);
    }
  }

  // When N/A, render a locked collapsed card
  const effectiveStatus = isNA ? 'na' : sectionStatus;

  return (
    <ADLSectionCard
      title="Repositioning" icon="🔄" sectionKey="repositioning"
      status={effectiveStatus}
      hasAmendments={amendments?.some(a => a.section === 'repositioning')}
      onSign={isNA ? undefined : onSign}
      disabled={disabled || isNA}
      attested={!!attestation}
    >
      <ADLAmendmentBanner amendments={amendments} section="repositioning" />

      {/* N/A banner (care plan flag) */}
      <ADLSectionNA
        section="repositioning"
        isNA={isNA}
        naReason={naReason}
        supportsAttestation={!isNA}
        attestation={attestation}
        isSectionSigned={sectionStatus === 'signed'}
        onAttest={onAttest}
        onRetract={onRetractAttestation}
      />

      {/* Hide form content when N/A or when attested with nothing to report */}
      {!isNA && (
        <>
          {entries.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {entries.map((e, i) => (
                <div key={e.adl_repo_id ?? i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', background: COLORS.bg, borderRadius: 8, marginBottom: 6,
                }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      {e.was_refused ? '❌ Refused' : `🔄 ${POSITION_LABELS[e.position] ?? e.position}`}
                    </span>
                    {e.assist_level && (
                      <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 8 }}>
                        {ASSIST_LABELS[e.assist_level]}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: COLORS.textLight }}>
                      {formatTime(e.performed_at ?? e.scheduled_at)}
                    </span>
                    {!disabled && (
                      <button onClick={() => startEdit(e)} style={editBtnStyle()}>✏️</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!disabled && !adding && !attestation && (
            <button onClick={startAdd} style={{
              width: '100%', padding: 10, borderRadius: 8,
              border: `2px dashed ${COLORS.border}`, background: 'none',
              fontSize: 13, color: COLORS.primary, fontWeight: 600, cursor: 'pointer',
            }}>+ Add Repositioning</button>
          )}

          {adding && (
            <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14 }}>
              {editTarget && (
                <div style={{ fontSize: 12, color: COLORS.warning, fontWeight: 600, marginBottom: 10 }}>
                  ✏️ Editing entry from {formatTime(editTarget.performed_at ?? editTarget.scheduled_at)}
                </div>
              )}

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Position</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {POSITIONS.map(pos => (
                    <button key={pos} onClick={() => setField('position', form.position === pos ? null : pos)}
                      style={{
                        padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: form.position === pos ? COLORS.primary : COLORS.surface,
                        color: form.position === pos ? '#fff' : COLORS.textMuted,
                        border: `1px solid ${form.position === pos ? COLORS.primary : COLORS.border}`,
                      }}>
                      {POSITION_LABELS[pos] ?? pos}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Assist Level</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {ASSIST_OPTIONS.map(a => (
                    <button key={a} onClick={() => setField('assist_level', form.assist_level === a ? null : a)}
                      style={{
                        padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        background: form.assist_level === a ? COLORS.primary : COLORS.surface,
                        color: form.assist_level === a ? '#fff' : COLORS.textMuted,
                        border: `1px solid ${form.assist_level === a ? COLORS.primary : COLORS.border}`,
                      }}>
                      {ASSIST_LABELS[a]}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <ToggleChip label="Skin Intact" active={form.skin_intact} onToggle={() => setField('skin_intact', !form.skin_intact)} color={COLORS.accent} />
                <ToggleChip label="Refused"     active={form.was_refused} onToggle={() => setField('was_refused', !form.was_refused)} color={COLORS.danger} />
              </div>

              <input type="text" value={form.notes} onChange={e => setField('notes', e.target.value)}
                placeholder="Notes (optional)" autoComplete="off" style={{ ...inputStyle, marginBottom: 10 }} />

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={cancelForm} style={cancelBtnStyle}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={saveBtnStyle(saving)}>
                  {saving ? '⏳ Saving…' : editTarget ? 'Update Entry' : 'Save Entry'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </ADLSectionCard>
  );
}


// ============================================================
// FluidIntakeForm
// Phase 5d: supports nothing-to-report attestation
// 2026-06-08: now also consumes the care-plan isNA flag
// ============================================================

export function FluidIntakeForm({
  adlRecordId, initialData = [], sectionStatus, amendments,
  onSign, disabled, onDataSaved,
  // Phase 5d props
  isNA = false, naReason = '',
  attestation = null, onAttest, onRetractAttestation,
}) {
  const [entries, setEntries]       = useState(initialData);
  const [adding, setAdding]         = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState(EMPTY_FLUID_FORM);

  function setField(k, v) { setForm(p => ({ ...p, [k]: v })); }

  const totalMl = entries.filter(e => !e.refused).reduce((sum, e) => sum + (parseInt(e.amount_ml) || 0), 0);

  function startAdd() {
    setEditTarget(null);
    setForm(EMPTY_FLUID_FORM);
    setAdding(true);
  }

  function startEdit(entry) {
    setEditTarget(entry);
    setForm({
      fluid_type: entry.fluid_type ?? null,
      amount_ml:  entry.amount_ml  ?? '',
      method:     entry.method     ?? 'oral',
      refused:    !!entry.refused,
      notes:      entry.notes      ?? '',
    });
    setAdding(true);
  }

  function cancelForm() {
    setAdding(false);
    setEditTarget(null);
    setForm(EMPTY_FLUID_FORM);
  }

  async function handleSave() {
    if (!form.fluid_type) return;
    try {
      setSaving(true);
      const payload = { ...form };
      if (editTarget) payload.adl_fluid_id = editTarget.adl_fluid_id;

      const data = await saveFluidIntake(adlRecordId, payload);
      if (data.success) {
        if (editTarget) {
          setEntries(prev => prev.map(e =>
            e.adl_fluid_id === editTarget.adl_fluid_id ? { ...e, ...form } : e
          ));
        } else {
          setEntries(prev => [...prev, { adl_fluid_id: data.adl_fluid_id, ...form, recorded_at: new Date().toISOString() }]);
        }
        cancelForm();
        onDataSaved?.();
      }
    } catch (err) {
      console.error('Fluid intake save failed:', err.message);
    } finally {
      setSaving(false);
    }
  }

  const effectiveStatus = isNA ? 'na' : sectionStatus;

  return (
    <ADLSectionCard
      title="Fluid Intake" icon="💧" sectionKey="fluid_intake"
      status={effectiveStatus}
      hasAmendments={amendments?.some(a => a.section === 'fluid_intake')}
      onSign={isNA ? undefined : onSign} disabled={disabled || isNA}
      attested={!!attestation}
    >
      <ADLAmendmentBanner amendments={amendments} section="fluid_intake" />

      {/* N/A (care plan flag) + nothing-to-report attestation */}
      <ADLSectionNA
        section="fluid_intake"
        isNA={isNA}
        naReason={naReason}
        supportsAttestation={!isNA}
        attestation={attestation}
        isSectionSigned={sectionStatus === 'signed'}
        onAttest={onAttest}
        onRetract={onRetractAttestation}
      />

      {/* Hide form content when N/A or attested */}
      {!isNA && !attestation && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: COLORS.primaryLight, borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.primary }}>Total Fluid Intake</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.primary }}>{totalMl} mL</span>
          </div>

          {entries.map((e, i) => (
            <div key={e.adl_fluid_id ?? i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 12px', background: COLORS.bg, borderRadius: 8, marginBottom: 6,
            }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  {e.refused ? '❌ Refused — ' : ''}{e.fluid_type}
                </span>
                <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 8 }}>{e.method}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>
                  {e.refused ? '—' : `${e.amount_ml} mL`}
                </span>
                {!disabled && (
                  <button onClick={() => startEdit(e)} style={editBtnStyle()}>✏️</button>
                )}
              </div>
            </div>
          ))}

          {!disabled && !adding && (
            <button onClick={startAdd} style={{
              width: '100%', padding: 10, borderRadius: 8,
              border: `2px dashed ${COLORS.border}`, background: 'none',
              fontSize: 13, color: COLORS.primary, fontWeight: 600, cursor: 'pointer',
            }}>+ Add Fluid Entry</button>
          )}

          {adding && (
            <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14, marginTop: 8 }}>
              {editTarget && (
                <div style={{ fontSize: 12, color: COLORS.warning, fontWeight: 600, marginBottom: 10 }}>
                  ✏️ Editing: {editTarget.fluid_type}
                </div>
              )}

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Fluid Type</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {FLUID_TYPES.map(ft => (
                    <button key={ft} onClick={() => setField('fluid_type', form.fluid_type === ft ? null : ft)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: form.fluid_type === ft ? COLORS.primary : COLORS.surface,
                        color: form.fluid_type === ft ? '#fff' : COLORS.textMuted,
                        border: `1px solid ${form.fluid_type === ft ? COLORS.primary : COLORS.border}`,
                      }}>{ft}</button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Amount (mL)</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  {FLUID_PRESETS.map(ml => (
                    <button key={ml} onClick={() => setField('amount_ml', ml)}
                      style={{
                        flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: form.amount_ml === ml ? COLORS.primary : COLORS.surface,
                        color: form.amount_ml === ml ? '#fff' : COLORS.textMuted,
                        border: `1px solid ${form.amount_ml === ml ? COLORS.primary : COLORS.border}`,
                      }}>{ml}</button>
                  ))}
                </div>
                <input type="number" value={form.amount_ml}
                  onChange={e => setField('amount_ml', parseInt(e.target.value) || '')}
                  placeholder="Or enter custom mL" autoComplete="off" style={inputStyle} />
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {['oral', 'tube', 'iv'].map(m => (
                  <button key={m} onClick={() => setField('method', m)} style={{
                    flex: 1, padding: '6px 0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: form.method === m ? COLORS.primary : COLORS.surface,
                    color: form.method === m ? '#fff' : COLORS.textMuted,
                    border: `1px solid ${form.method === m ? COLORS.primary : COLORS.border}`,
                  }}>{m.toUpperCase()}</button>
                ))}
                <ToggleChip label="Refused" active={form.refused} onToggle={() => setField('refused', !form.refused)} color={COLORS.danger} />
              </div>

              <input type="text" value={form.notes} onChange={e => setField('notes', e.target.value)}
                placeholder="Notes (optional)" autoComplete="off" style={{ ...inputStyle, marginBottom: 10 }} />

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={cancelForm} style={cancelBtnStyle}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={saveBtnStyle(saving)}>
                  {saving ? '⏳ Saving…' : editTarget ? 'Update Entry' : 'Save Entry'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </ADLSectionCard>
  );
}


// ============================================================
// BowelUrinationForm
// Phase 5d: split attestation props per sub-section
// 2026-06-08: split isNA flags per sub-section
// ============================================================

export function BowelUrinationForm({
  adlRecordId, initialBowel, initialUrination,
  bowelStatus, urinationStatus, amendments, onSign,
  bowelDisabled = false, urinationDisabled = false,
  // Phase 5d props — separate per sub-section
  bowelIsNA = false,          urinationIsNA = false,
  bowelNaReason = '',         urinationNaReason = '',
  bowelAttestation = null,    urinationAttestation = null,
  onAttestBowel,              onAttestUrination,
  onRetractBowel,             onRetractUrination,
}) {

  const bowelSaveFn = useCallback(async (d) => {
    await saveBowel(adlRecordId, d);
  }, [adlRecordId]);

  const urineSaveFn = useCallback(async (d) => {
    await saveUrination(adlRecordId, d);
  }, [adlRecordId]);

  const { formData: bowelData, setField: setBowel, saveStatus: bowelSave } = useADLForm(
    initialBowel ?? { had_bm: false, size: null, consistency: null, color: null, blood_present: false, incontinent: false, ostomy_present: false, notes: '' },
    bowelSaveFn, { disabled: bowelDisabled || !!bowelAttestation }
  );

  const { formData: urineData, setField: setUrine, saveStatus: urineSave } = useADLForm(
    initialUrination ?? { output_type: null, amount_ml: '', incontinent_episodes: 0, catheter_type: null, urine_color: null, odor_present: false, notes: '' },
    urineSaveFn, { disabled: urinationDisabled || !!urinationAttestation, validate: (d) => !!d.output_type }
  );

  return (
    <>
      {/* ── BOWEL ── */}
      <ADLSectionCard
        title="Bowel" icon="📋" sectionKey="bowel"
        status={bowelIsNA ? 'na' : bowelStatus} saveStatus={bowelSave}
        hasAmendments={amendments?.some(a => a.section === 'bowel')}
        onSign={bowelIsNA ? undefined : (k) => onSign?.(k.startsWith('amend_') ? k : 'bowel')}
        disabled={bowelDisabled || bowelIsNA}
        attested={!!bowelAttestation}
      >
        <ADLAmendmentBanner amendments={amendments} section="bowel" />

        <ADLSectionNA
          section="bowel"
          isNA={bowelIsNA}
          naReason={bowelNaReason}
          supportsAttestation={!bowelIsNA}
          attestation={bowelAttestation}
          isSectionSigned={bowelStatus === 'signed'}
          onAttest={onAttestBowel}
          onRetract={onRetractBowel}
        />

        {!bowelIsNA && !bowelAttestation && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <ToggleChip label="Had BM"      active={bowelData.had_bm}         onToggle={() => !bowelDisabled && setBowel('had_bm', !bowelData.had_bm)}                 color={COLORS.accent}  />
              <ToggleChip label="Incontinent" active={bowelData.incontinent}    onToggle={() => !bowelDisabled && setBowel('incontinent', !bowelData.incontinent)}       color={COLORS.warning} />
              <ToggleChip label="Ostomy"      active={bowelData.ostomy_present} onToggle={() => !bowelDisabled && setBowel('ostomy_present', !bowelData.ostomy_present)} color={COLORS.primary} />
              <ToggleChip label="Blood"       active={bowelData.blood_present}  onToggle={() => !bowelDisabled && setBowel('blood_present', !bowelData.blood_present)}   color={COLORS.danger}  />
            </div>

            {bowelData.had_bm && (
              <>
                <ChipRow label="Size"        options={BOWEL_SIZES.map(b => ({ key: b.code, label: b.label }))}            value={bowelData.size}        onChange={v => !bowelDisabled && setBowel('size', v)} />
                <ChipRow label="Consistency" options={CONSISTENCY_OPTIONS.map(c => ({ key: c.toLowerCase(), label: c }))} value={bowelData.consistency} onChange={v => !bowelDisabled && setBowel('consistency', v)} />
                <ChipRow label="Color"       options={BM_COLOR_OPTIONS.map(c => ({ key: c.toLowerCase(), label: c }))}    value={bowelData.color}       onChange={v => !bowelDisabled && setBowel('color', v)} />
              </>
            )}

            <input type="text" value={bowelData.notes ?? ''} onChange={e => !bowelDisabled && setBowel('notes', e.target.value)}
              placeholder="Notes (optional)" disabled={bowelDisabled} autoComplete="off" style={inputStyle} />
          </>
        )}
      </ADLSectionCard>

      {/* ── URINATION ── */}
      <ADLSectionCard
        title="Urination" icon="🚽" sectionKey="urination"
        status={urinationIsNA ? 'na' : urinationStatus} saveStatus={urineSave}
        hasAmendments={amendments?.some(a => a.section === 'urination')}
        onSign={urinationIsNA ? undefined : (k) => onSign?.(k.startsWith('amend_') ? k : 'urination')}
        disabled={urinationDisabled || urinationIsNA}
        attested={!!urinationAttestation}
      >
        <ADLAmendmentBanner amendments={amendments} section="urination" />

        <ADLSectionNA
          section="urination"
          isNA={urinationIsNA}
          naReason={urinationNaReason}
          supportsAttestation={!urinationIsNA}
          attestation={urinationAttestation}
          isSectionSigned={urinationStatus === 'signed'}
          onAttest={onAttestUrination}
          onRetract={onRetractUrination}
        />

        {!urinationIsNA && !urinationAttestation && (
          <>
            <ChipRow label="Output Type"
              options={URINE_TYPES.map(u => ({ key: u.code, label: `${u.icon} ${u.label}` }))}
              value={urineData.output_type}
              onChange={v => !urinationDisabled && setUrine('output_type', v)} />

            {urineData.output_type === 'catheter' && (
              <ChipRow label="Catheter Type"
                options={['foley', 'suprapubic', 'condom', 'other'].map(c => ({ key: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))}
                value={urineData.catheter_type}
                onChange={v => !urinationDisabled && setUrine('catheter_type', v)} />
            )}

            {urineData.output_type === 'incontinent' && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Episodes</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => !urinationDisabled && setUrine('incontinent_episodes', n)}
                      style={{
                        flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 13,
                        fontWeight: 700, cursor: urinationDisabled ? 'default' : 'pointer',
                        background: urineData.incontinent_episodes === n ? COLORS.warning : COLORS.surface,
                        color: urineData.incontinent_episodes === n ? '#fff' : COLORS.textMuted,
                        border: `1px solid ${urineData.incontinent_episodes === n ? COLORS.warning : COLORS.border}`,
                      }}>{n}</button>
                  ))}
                </div>
              </div>
            )}

            <ChipRow label="Urine Color"
              options={URINE_COLORS.map(c => ({ key: c.toLowerCase().replace(' ', '_'), label: c }))}
              value={urineData.urine_color}
              onChange={v => !urinationDisabled && setUrine('urine_color', v)} />

            <div style={{ marginBottom: 10 }}>
              <ToggleChip label="Odor Present" active={urineData.odor_present}
                onToggle={() => !urinationDisabled && setUrine('odor_present', !urineData.odor_present)}
                color={COLORS.warning} />
            </div>

            <input type="text" value={urineData.notes ?? ''} onChange={e => !urinationDisabled && setUrine('notes', e.target.value)}
              placeholder="Notes (optional)" disabled={urinationDisabled} autoComplete="off" style={inputStyle} />
          </>
        )}
      </ADLSectionCard>
    </>
  );
}


// ============================================================
// SkinChecksForm
// Phase 5d: supports isNA flag + nothing-to-report attestation
// ============================================================

export function SkinChecksForm({
  adlRecordId, initialData = [], sectionStatus, amendments,
  onSign, disabled, onDataSaved,
  // Phase 5d props
  isNA = false, naReason = '',
  attestation = null, onAttest, onRetractAttestation,
}) {
  const [entries, setEntries]       = useState(initialData);
  const [adding, setAdding]         = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [form, setFormState]        = useState(EMPTY_SKIN_FORM);

  function setField(k, v) { setFormState(p => ({ ...p, [k]: v })); }

  const overallPass = entries.length === 0 || entries.every(e => e.result === 'pass');

  function startAdd() {
    setEditTarget(null);
    setFormState(EMPTY_SKIN_FORM);
    setAdding(true);
  }

  function startEdit(entry) {
    setEditTarget(entry);
    setFormState({
      result:               entry.result ?? 'pass',
      body_location:        entry.body_location ?? '',
      description:          entry.description ?? '',
      wound_care_performed: !!entry.wound_care_performed,
      wound_care_notes:     entry.wound_care_notes ?? '',
      skin_cream_applied:   !!entry.skin_cream_applied,
      nurse_notified:       !!entry.nurse_notified,
    });
    setAdding(true);
  }

  function cancelForm() {
    setAdding(false);
    setEditTarget(null);
    setFormState(EMPTY_SKIN_FORM);
  }

  async function handleSave() {
    try {
      setSaving(true);
      const payload = { ...form };
      if (editTarget) payload.adl_skin_check_id = editTarget.adl_skin_check_id;

      const data = await saveSkinCheck(adlRecordId, payload);
      if (data.success) {
        if (editTarget) {
          setEntries(prev => prev.map(e =>
            e.adl_skin_check_id === editTarget.adl_skin_check_id ? { ...e, ...form } : e
          ));
        } else {
          setEntries(prev => [...prev, { adl_skin_check_id: data.adl_skin_check_id, ...form }]);
        }
        cancelForm();
        onDataSaved?.();
      }
    } catch (err) {
      console.error('Skin check save failed:', err.message);
    } finally {
      setSaving(false);
    }
  }

  const effectiveStatus = isNA ? 'na' : sectionStatus;

  return (
    <ADLSectionCard
      title="Skin Checks" icon="👁️" sectionKey="skin_checks"
      status={effectiveStatus}
      hasAmendments={amendments?.some(a => a.section === 'skin_checks')}
      onSign={isNA ? undefined : onSign}
      disabled={disabled || isNA}
      attested={!!attestation}
    >
      <ADLAmendmentBanner amendments={amendments} section="skin_checks" />

      <ADLSectionNA
        section="skin_checks"
        isNA={isNA}
        naReason={naReason}
        supportsAttestation={!isNA}
        attestation={attestation}
        isSectionSigned={sectionStatus === 'signed'}
        onAttest={onAttest}
        onRetract={onRetractAttestation}
      />

      {!isNA && !attestation && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: overallPass ? COLORS.accentLight : COLORS.dangerLight,
            border: `1px solid ${overallPass ? COLORS.accentAlpha : COLORS.dangerAlpha}`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          }}>
            <span style={{ fontSize: 18 }}>{overallPass ? '✅' : '⚠️'}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: overallPass ? COLORS.accent : COLORS.danger }}>
              {entries.length === 0
                ? 'No skin check recorded yet'
                : overallPass
                  ? 'Skin intact — no issues noted'
                  : `${entries.filter(e => e.result === 'fail').length} issue(s) documented`}
            </span>
          </div>

          {entries.filter(e => e.result === 'fail').map((e, i) => (
            <div key={e.adl_skin_check_id ?? i} style={{
              background: COLORS.dangerLight, borderRadius: 8, padding: '10px 12px', marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.danger, marginBottom: 4 }}>
                    ⚠️ {e.body_location || 'Location not specified'}
                  </div>
                  {e.description && <div style={{ fontSize: 12, color: COLORS.text }}>{e.description}</div>}
                  {e.nurse_notified && <div style={{ fontSize: 11, color: COLORS.primary, marginTop: 4 }}>✓ Nurse notified</div>}
                </div>
                {!disabled && (
                  <button onClick={() => startEdit(e)} style={editBtnStyle()}>✏️</button>
                )}
              </div>
            </div>
          ))}

          {entries.filter(e => e.result === 'pass').map((e, i) => (
            <div key={e.adl_skin_check_id ?? i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: COLORS.accentLight, borderRadius: 8, padding: '8px 12px', marginBottom: 6,
            }}>
              <span style={{ fontSize: 13, color: COLORS.accent, fontWeight: 600 }}>
                ✅ {e.body_location ? `Pass — ${e.body_location}` : 'Skin intact — Pass'}
              </span>
              {!disabled && (
                <button onClick={() => startEdit(e)} style={editBtnStyle()}>✏️</button>
              )}
            </div>
          ))}

          {!disabled && !adding && (
            <button onClick={startAdd} style={{
              width: '100%', padding: 10, borderRadius: 8,
              border: `2px dashed ${COLORS.border}`, background: 'none',
              fontSize: 13, color: COLORS.primary, fontWeight: 600, cursor: 'pointer',
            }}>+ Add Skin Check Finding</button>
          )}

          {adding && (
            <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14, marginTop: 8 }}>
              {editTarget && (
                <div style={{ fontSize: 12, color: COLORS.warning, fontWeight: 600, marginBottom: 10 }}>
                  ✏️ Editing: {editTarget.body_location || 'skin check entry'}
                </div>
              )}
              <ChipRow label="Result"
                options={[{ key: 'pass', label: '✅ Pass' }, { key: 'fail', label: '⚠️ Fail / Issue' }]}
                value={form.result} onChange={v => v && setField('result', v)} />
              <input type="text" value={form.body_location} onChange={e => setField('body_location', e.target.value)}
                placeholder="Body location (e.g. Sacrum, Left heel)" autoComplete="off" style={{ ...inputStyle, marginBottom: 8 }} />
              <textarea value={form.description} onChange={e => setField('description', e.target.value)}
                placeholder="Description of finding" rows={2} autoComplete="off"
                style={{ ...inputStyle, resize: 'none', marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <ToggleChip label="Wound Care Done"    active={form.wound_care_performed} onToggle={() => setField('wound_care_performed', !form.wound_care_performed)} color={COLORS.primary} />
                <ToggleChip label="Skin Cream Applied" active={form.skin_cream_applied}   onToggle={() => setField('skin_cream_applied', !form.skin_cream_applied)}     color={COLORS.primary} />
                <ToggleChip label="Nurse Notified"     active={form.nurse_notified}       onToggle={() => setField('nurse_notified', !form.nurse_notified)}             color={COLORS.warning} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={cancelForm} style={cancelBtnStyle}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={saveBtnStyle(saving)}>
                  {saving ? '⏳ Saving…' : editTarget ? 'Update Finding' : 'Save Finding'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* When attested, show a simple confirmed state */}
      {!isNA && !!attestation && !adding && (
        <p style={{ fontSize: 13, color: COLORS.textMuted, margin: 0 }}>
          No skin findings to document this shift.
        </p>
      )}
    </ADLSectionCard>
  );
}


// ============================================================
// SafetyChecksForm
// Phase 5d: supports nothing-to-report attestation
// 2026-06-08: now also consumes the care-plan isNA flag
// ============================================================

export function SafetyChecksForm({
  adlRecordId, checklistItems = [], initialData = [], sectionStatus,
  amendments, onSign, disabled, onDataSaved, onCheckedCountChange,
  // Phase 5d props
  isNA = false, naReason = '',
  attestation = null, onAttest, onRetractAttestation,
}) {

  const buildInitial = () => {
    const state = {};
    checklistItems.forEach(item => {
      const existing = initialData.find(d => d.checklist_item_id === item.checklist_item_id);
      state[item.checklist_item_id] = {
        is_checked: existing?.is_checked ?? false,
        notes:      existing?.notes ?? '',
      };
    });
    return state;
  };

  const saveFn = useCallback(async (formData) => {
    const items = Object.entries(formData).map(([id, val]) => ({
      checklist_item_id: parseInt(id),
      is_checked:        val.is_checked,
      notes:             val.notes || null,
    }));
    await saveSafetyChecks(adlRecordId, items);
  }, [adlRecordId]);

  const { formData, setFields, saveStatus } = useADLForm(
    buildInitial(),
    saveFn,
    { disabled: disabled || !!attestation }
  );

  const checkedCount = Object.values(formData).filter(v => v.is_checked).length;
  const totalCount   = checklistItems.length;

  useEffect(() => {
    onCheckedCountChange?.(checkedCount);
  }, [checkedCount, onCheckedCountChange]);

  function toggle(itemId) {
    if (disabled || attestation) return;
    setFields({ [itemId]: { ...formData[itemId], is_checked: !formData[itemId]?.is_checked } });
  }

  const groupedItems = SAFETY_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = checklistItems.filter(i => i.category === cat);
    return acc;
  }, {});

  return (
    <ADLSectionCard
      title="Safety Checks" icon="✅" sectionKey="safety_checks"
      status={isNA ? 'na' : sectionStatus} saveStatus={saveStatus}
      hasAmendments={amendments?.some(a => a.section === 'safety_checks')}
      onSign={isNA ? undefined : onSign} disabled={disabled || isNA}
      attested={!!attestation}
    >
      <ADLAmendmentBanner amendments={amendments} section="safety_checks" />

      <ADLSectionNA
        section="safety_checks"
        isNA={isNA}
        naReason={naReason}
        supportsAttestation={!isNA}
        attestation={attestation}
        isSectionSigned={sectionStatus === 'signed'}
        onAttest={onAttest}
        onRetract={onRetractAttestation}
      />

      {!isNA && !attestation && (
        <>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: COLORS.primaryLight, borderRadius: 8, padding: '8px 14px', marginBottom: 14,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.primary }}>Items Checked</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: COLORS.primary }}>{checkedCount}/{totalCount}</span>
          </div>

          {Object.entries(groupedItems).map(([category, items]) => {
            if (items.length === 0) return null;
            return (
              <div key={category} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>
                  {category}
                </div>
                {items.map(item => {
                  const checked = !!formData[item.checklist_item_id]?.is_checked;
                  return (
                    <button key={item.checklist_item_id} onClick={() => toggle(item.checklist_item_id)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px', marginBottom: 6, borderRadius: 8,
                        border: `1px solid ${checked ? COLORS.accent : COLORS.border}`,
                        background: checked ? COLORS.accentLight : COLORS.surface,
                        cursor: disabled || attestation ? 'default' : 'pointer', textAlign: 'left',
                      }}
                    >
                      <div style={{
                        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                        background: checked ? COLORS.accent : COLORS.surface,
                        border: `2px solid ${checked ? COLORS.accent : COLORS.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, color: '#fff', fontWeight: 700,
                      }}>
                        {checked ? '✓' : ''}
                      </div>
                      <span style={{ fontSize: 13, color: checked ? COLORS.text : COLORS.textMuted, fontWeight: checked ? 600 : 400 }}>
                        {item.item_label}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </ADLSectionCard>
  );
}